
(function () {
  'use strict';

  var app = null;
  var wishByVariant = new Map();   // variantId(string) -> wishlistItemId (server truth)
  var wishItems = [];              // normalized [{ productId, variantId, handle }]
  var pending = new Map();         // variantId -> desired bool (optimistic target, until confirmed)
  var renderedSlots = new Map();   // variantId -> wishlist-page slot element
  var queued = new Set();          // variants with a reconcile already scheduled
  var opChain = Promise.resolve(); // serialises the actual API calls (background, this tab)

  // Shared view: /pages/wishlist?wishlist=<publicId> shows someone else's (read-only) list.
  var sharedId = (function () {
    if (!document.getElementById('wishlist-items-container')) return null;
    try { return new URLSearchParams(window.location.search).get('wishlist') || null; }
    catch (e) { return null; }
  })();
  var sharedItems = [];            // normalized items of the shared wishlist
  var removing = new Set();        // wishlist page: variants whose removal is in flight

  // Cross-tab serialisation: hold a Web Lock around every real write so that two tabs
  // sharing one session can't fire concurrent writes (King drops one of them). The lock
  // is held until the write is server-confirmed (waitReal / waitGone inside `fn`).
  function lockWrite(fn) {
    var run = function () {
      return adoptSharedSession().then(fn).then(function (r) { notifyTabs(); return r; });
    };
    if (navigator.locks && navigator.locks.request) {
      return navigator.locks.request('wk-wishlist-write', { mode: 'exclusive' }, run);
    }
    return Promise.resolve().then(run); // fallback: per-tab opChain still serialises
  }

  // FIRST-SESSION race (also reproducible on demo.swish.app): the King SDK keeps the
  // guest session id in memory (`apis.wishlist.headers['x-appmate-sid']`). In a fresh
  // (incognito) window every tab starts WITHOUT one, so each tab's first add creates its
  // OWN server session; whichever tab writes `wk_session_id` to localStorage last wins
  // and the other tab's items are orphaned. Fix: inside the write lock (so the first tab
  // has finished creating + storing its session), adopt the session id another tab
  // stored, then refetch before writing — every tab ends up on ONE session.
  var SID_KEY = 'wk_session_id';

  function syncSession() {
    try {
      var api = app && app.apis && app.apis.wishlist;
      if (!api || !api.headers || api.headers['x-appmate-cid']) return false; // logged in → keyed by customer
      var stored = window.localStorage.getItem(SID_KEY);
      if (!stored || api.headers['x-appmate-sid'] === stored) return false;
      api.setSessionId(stored);
      api._pendingSessionId = undefined;
      if (app.apis.tracking && app.apis.tracking.setSessionId) app.apis.tracking.setSessionId(stored);
      return true;
    } catch (e) { logErr(e); return false; }
  }

  // Resolves on the next observeWishlist emission (or after `ms`).
  var emitWaiters = [];
  function nextEmit(ms) {
    return new Promise(function (resolve) {
      emitWaiters.push(resolve);
      setTimeout(resolve, ms);
    });
  }

  function adoptSharedSession() {
    if (!syncSession()) return Promise.resolve();
    var fresh = nextEmit(4000);
    return Promise.resolve(app.state.revalidate()).then(function () { return fresh; }, function (e) { logErr(e); });
  }

  // Another tab created/changed the session → follow it right away (hearts + count).
  window.addEventListener('storage', function (e) {
    if (e.key !== SID_KEY || !app) return;
    if (syncSession()) Promise.resolve(app.state.revalidate()).catch(logErr);
  });

  // Keep OTHER tabs' hearts + header count fresh. Mobile browsers (real devices) freeze
  // background tabs, so they miss any live cross-tab update and the SDK never refetches
  // on its own → hearts/count stay stale until reload (the wishlist page looked right
  // only because it refetches on open). So: (1) after every confirmed write, tell other
  // tabs via BroadcastChannel; (2) whenever a tab becomes visible again (tab switch,
  // app resume, bfcache restore), refetch from the server.
  var channel = null;
  try { if (window.BroadcastChannel) channel = new BroadcastChannel('wk-wishlist'); } catch (e) { channel = null; }

  function notifyTabs() {
    try { if (channel) channel.postMessage('changed'); } catch (e) { logErr(e); }
  }

  var lastRefresh = 0;
  function refreshFromServer() {
    if (!app || document.visibilityState === 'hidden') return;
    var now = Date.now();
    if (now - lastRefresh < 1000) return; // visibilitychange + focus often fire together
    lastRefresh = now;
    syncSession();
    Promise.resolve(app.state.revalidate()).catch(logErr);
  }

  if (channel) channel.onmessage = function () { lastRefresh = 0; refreshFromServer(); };
  document.addEventListener('visibilitychange', refreshFromServer);
  window.addEventListener('focus', refreshFromServer);
  window.addEventListener('pageshow', function (e) { if (e.persisted) { lastRefresh = 0; refreshFromServer(); } });

  function logErr(e) { if (window.console) console.warn('[wishlist]', e); }
  function str(v) { return String(v == null ? '' : v); }
  function num(v) { var n = parseInt(v, 10); return isNaN(n) ? v : n; }
  function isTemp(id) { return id == null || String(id).indexOf('temp-') === 0; }

  function iVariant(i) { return str(i.variantId != null ? i.variantId : (i.variant_id != null ? i.variant_id : '')); }
  function iProduct(i) { return str(i.productId != null ? i.productId : (i.product && i.product.id != null ? i.product.id : '')); }
  function iItemId(i)  { return i.id != null ? i.id : (i.wishlistItemId != null ? i.wishlistItemId : null); }
  function iHandle(i)  { return str((i.product && i.product.handle) || i.productHandle || ''); }

  function handleOf(btn) {
    var m = str(btn.getAttribute('data-du')).split('?')[0].match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : '';
  }

  function whenReady(fn) {
    if (window.WishlistKing && window.WishlistKing.state) { fn(window.WishlistKing); return; }
    var tries = 0;
    var t = setInterval(function () {
      if (window.WishlistKing && window.WishlistKing.state) { clearInterval(t); fn(window.WishlistKing); }
      else if (++tries > 300) { clearInterval(t); logErr('Wishlist King SDK not found'); }
    }, 50);
  }

  whenReady(function (a) {
    app = a;
    try { app.state.observeWishlist({ wishlistId: 'mine' }).subscribe(onWishlist); }
    catch (e) { logErr(e); }
    if (sharedId) {
      // An unknown/deleted id never emits (and King's observable ignores {error}),
      // so fall back to the empty state after a while instead of "Loading…" forever.
      var sharedTimer = setTimeout(function () { onSharedWishlist(null); }, 8000);
      try {
        app.state.observeWishlist({ wishlistId: sharedId }).subscribe(function (wl) {
          clearTimeout(sharedTimer);
          onSharedWishlist(wl);
        });
      } catch (e) { clearTimeout(sharedTimer); logErr(e); onSharedWishlist(null); }
    }
  });

  function normalize(it) { return { productId: iProduct(it), variantId: iVariant(it), handle: iHandle(it) }; }

  function onSharedWishlist(wl) {
    sharedItems = ((wl && wl.items) || []).map(normalize);
    renderPage();
  }

  function onWishlist(wl) {
    var items = (wl && wl.items) || [];
    wishByVariant = new Map();
    wishItems = [];
    items.forEach(function (it) {
      var n = normalize(it);
      if (n.variantId) wishByVariant.set(n.variantId, iItemId(it));
      wishItems.push(n);
    });
    // Clear optimistic state the server now reflects with a REAL (non-temp) id.
    pending.forEach(function (desired, vid) {
      var confirmed = desired ? (wishByVariant.has(vid) && !isTemp(wishByVariant.get(vid))) : !wishByVariant.has(vid);
      if (confirmed) pending.delete(vid);
    });
    renderState();
    renderPage();
    var waiters = emitWaiters; emitWaiters = [];
    waiters.forEach(function (r) { r(); });
  }

  // ---- Heart state ----------------------------------------------------------

  // A card being removed on the wishlist page keeps showing server truth until confirmed.
  function isOn(vid) {
    if (removing.has(vid)) return wishByVariant.has(vid);
    return pending.has(vid) ? pending.get(vid) : wishByVariant.has(vid);
  }

  function setWishlistButtonState(btn, on) {
    btn.classList.toggle('is-wishlisted', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
  }
  function paintBtn(btn) { setWishlistButtonState(btn, isOn(str(btn.getAttribute('data-epi')))); }

  function effectiveCount() {
    var set = new Set(wishByVariant.keys());
    pending.forEach(function (desired, vid) { if (removing.has(vid)) return; if (desired) set.add(vid); else set.delete(vid); });
    return set.size;
  }

  function renderState() {
    document.querySelectorAll('.js-wishlist-btn').forEach(paintBtn);
    var count = effectiveCount();
    document.querySelectorAll('.js-wishlist-count').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  // PDP: the heart is part of the product section's variant swap (#pdp-wishlist-<sid> in
  // sections/product-demo.liquid), so the server re-renders it with the right product id, url and
  // selected variant — including combined-listing pills, which switch to a SIBLING product. (The old
  // approach only copied the clicked variant id onto the heart and kept the previous product, so
  // after "Power Recline" it saved a Power variant under the Manual product → shown as the default
  // variant.) Repaint the freshly rendered heart once the swap is done.
  window.addEventListener('pdp:variant-swapped', renderState);

  // Product card: swatch change updates data-epi → repaint by the new variant.
  document.addEventListener('product-card:variant-change', function (e) {
    var btn = e.target.closest ? e.target.closest('.js-wishlist-btn') : null;
    if (btn) paintBtn(btn);
  });

  // ---- Click = instant optimistic paint + schedule a background reconcile ----

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('.js-wishlist-btn') : null;
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    // PDP heart mid-swap still describes the PREVIOUS variant/product — ignore until re-rendered.
    if (btn.closest('.pdp-swapping')) return;
    if (!app) { logErr('Wishlist King SDK not ready'); return; }

    var epi = str(btn.getAttribute('data-epi'));
    var empi = str(btn.getAttribute('data-empi'));
    if (!epi) return;

    // On the WISHLIST PAGE the heart means "remove this item". Like the app's default
    // page (demo.swish.app): the card stays as-is until the server confirms the removal,
    // and while ANY removal is in flight other heart clicks are ignored — no action.
    // (Optimistically dropping the card made it pop back when the server re-emitted.)
    // (Not in the shared view: there the heart toggles the VIEWER's own wishlist.)
    var onWishlistPage = !sharedId && !!(btn.closest && btn.closest('#wishlist-items-container'));
    if (onWishlistPage) {
      if (removing.size || !isOn(epi)) return; // busy → no action at all
      removing.add(epi);
      pending.set(epi, false);
      paintBusy();
      scheduleReconcile(epi, empi, handleOf(btn));
      var unlock = function () { removing.delete(epi); paintBusy(); renderState(); };
      opChain.then(unlock, unlock);
      return;
    }

    pending.set(epi, !isOn(epi)); // flip the target instantly
    renderState();                // heart + count update immediately — NO dimming, NO lock
    scheduleReconcile(epi, empi, handleOf(btn));
  });

  // Wishlist page: state hooks only (.wishlist-busy / .is-processing / aria-busy) — no
  // visual change, the theme's heart UI stays as-is.
  function paintBusy() {
    var box = document.getElementById('wishlist-items-container');
    if (box) {
      box.classList.toggle('wishlist-busy', removing.size > 0);
      box.setAttribute('aria-busy', removing.size > 0 ? 'true' : 'false');
    }
    renderedSlots.forEach(function (slot, vid) { slot.classList.toggle('is-processing', removing.has(vid)); });
  }

  // One reconcile per variant at a time; it always uses the LATEST target in `pending`.
  function scheduleReconcile(epi, empi, handle) {
    if (queued.has(epi)) return;  // a reconcile is already queued for this variant
    queued.add(epi);
    opChain = opChain.then(function () { return reconcile(epi, empi, handle); },
                           function () { return reconcile(epi, empi, handle); });
  }

  // Bring the server to match the optimistic target for one variant, waiting for the
  // real id so the NEXT queued op can't be dropped by the SDK.
  function reconcile(epi, empi, handle) {
    queued.delete(epi);
    var target = pending.has(epi) ? pending.get(epi) : wishByVariant.has(epi);
    var serverHas = wishByVariant.has(epi);
    if (target === serverHas) { pending.delete(epi); return; } // already in sync

    if (target) {
      // ADD under the cross-tab lock, then wait until it's confirmed with a real id
      // (so the next tab's write can't be dropped by the SDK).
      return lockWrite(function () {
        // Another tab may have added this exact variant while we waited for the lock.
        if (wishByVariant.has(epi) && !isTemp(wishByVariant.get(epi))) { pending.delete(epi); renderState(); return; }
        return Promise.resolve(app.addWishlistItem({ productId: num(empi), variantId: num(epi), productHandle: handle }))
          .then(function (r) {
            if (r && r.ok === false) { pending.delete(epi); renderState(); return; }
            showToast();
            return waitReal(epi, 6000);
          });
      }).catch(function (e) { pending.delete(epi); renderState(); logErr(e); });
    }
    // REMOVE — the King SDK silently DROPS removes under load, so we confirm each one
    // (waitGone) and RETRY until the item is actually gone.
    return removeWithRetry(epi, 0);
  }

  function removeWithRetry(epi, attempt) {
    if (!wishByVariant.has(epi)) { pending.delete(epi); return Promise.resolve(); } // already gone
    var wid = wishByVariant.get(epi);
    if (isTemp(wid)) { // not confirmed yet → wait for the real id, then remove
      return waitReal(epi, 6000).then(function () { return removeWithRetry(epi, attempt); });
    }
    return lockWrite(function () {
      if (!wishByVariant.has(epi)) return Promise.resolve();        // gone while waiting for the lock
      var id = wishByVariant.get(epi);
      if (isTemp(id)) return waitReal(epi, 6000);                   // reverted to temp → re-sync, retry
      return Promise.resolve(app.removeWishlistItem({ wishlistItemId: id }))
        .then(function () { return waitGone(epi, 3500); });
    })
      .then(function () {
        if (wishByVariant.has(epi) && attempt < 4) return removeWithRetry(epi, attempt + 1);
        pending.delete(epi); renderState();
      })
      .catch(function (e) {
        logErr(e);
        if (attempt < 4) return removeWithRetry(epi, attempt + 1);
        pending.delete(epi); renderState();
      });
  }

  // Resolve once this variant is GONE from the observed wishlist (confirmed removal).
  function waitGone(epi, ms) {
    if (!wishByVariant.has(epi)) return Promise.resolve();
    return new Promise(function (resolve) {
      var n = 0;
      var t = setInterval(function () {
        if (!wishByVariant.has(epi)) { clearInterval(t); resolve(); }
        else if (++n > ms / 100) { clearInterval(t); resolve(); }
      }, 100);
    });
  }

  // Resolve once this variant has a REAL (non-temp) id in the observed wishlist.
  function waitReal(epi, ms) {
    var wid = wishByVariant.get(epi);
    if (!isTemp(wid)) return Promise.resolve(wid);
    return new Promise(function (resolve) {
      var n = 0;
      var t = setInterval(function () {
        var w = wishByVariant.get(epi);
        if (!isTemp(w)) { clearInterval(t); resolve(w); }
        else if (++n > ms / 100) { clearInterval(t); resolve(w || null); }
      }, 100);
    });
  }

  // ---- Toast: "Added to wishlist" (feature #503) ----------------------------

  var toastEl = null, toastTimer = null;
  function showToast() {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wishlist-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.innerHTML =
        '<svg class="wishlist-toast__icon" viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true"><path d="M17.4117 7.87086C16.5395 12.8962 9.99578 17.0833 9.99578 17.0833C9.99578 17.0833 3.40385 12.8961 2.57985 7.87113C1.75585 2.84621 7.52379 1.17103 9.99578 5.06625C12.4678 1.17096 18.2839 2.84553 17.4117 7.87086Z" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '<span class="wishlist-toast__text">Added to wishlist</span>' +
        '<a class="wishlist-toast__link" href="/pages/wishlist">View wishlist</a>';
      document.body.appendChild(toastEl);
    }
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-visible'); }, 3000);
  }

  // ---- Wishlist page (incremental diff — never blanks) -----------------------

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function renderPage() {
    var box = document.getElementById('wishlist-items-container');
    if (!box) return;
    var items = sharedId ? sharedItems : wishItems;

    if (!items.length) {
      var emptyText = (sharedId && box.getAttribute('data-shared-empty')) || box.getAttribute('data-empty') || 'Your wishlist is empty.';
      box.innerHTML = '<p class="wishlist-empty">' + esc(emptyText) + '</p>';
      renderedSlots.clear();
      return;
    }
    if (renderedSlots.size === 0) box.innerHTML = '';

    var wanted = {};
    items.forEach(function (i) { wanted[i.variantId] = true; });

    renderedSlots.forEach(function (slot, vid) {
      if (!wanted[vid]) { slot.remove(); renderedSlots.delete(vid); }
    });

    items.forEach(function (i) {
      if (!i.handle || renderedSlots.has(i.variantId)) return;
      var slot = document.createElement('div');
      slot.className = 'wishlist-grid__item';
      renderedSlots.set(i.variantId, slot);
      box.appendChild(slot);

      var url = window.location.origin + '/products/' + encodeURIComponent(i.handle) + '?section_id=wishlist-card';
      if (i.variantId) url += '&variant=' + encodeURIComponent(i.variantId);

      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var card = tmp.querySelector('.cc-card');
          if (!card) { slot.remove(); renderedSlots.delete(i.variantId); return; }
          var heart = card.querySelector('.js-wishlist-btn');
          if (heart) {
            heart.setAttribute('data-empi', i.productId);
            if (i.variantId) heart.setAttribute('data-epi', i.variantId);
          }
          slot.appendChild(card);
          if (heart) paintBtn(heart);
        })
        .catch(function (e) { logErr(e); slot.remove(); renderedSlots.delete(i.variantId); });
    });
  }

  // ---- Wishlist page header: shared view + Share dialog ----------------------
  // Guests see a "Log in to Share" link (rendered by Liquid); logged-in customers
  // get the Share button. The share link points at THIS page with ?wishlist=<publicId>.

  function initPageHeader() {
    if (!document.getElementById('wishlist-items-container')) return;

    if (sharedId) {
      // Shared view: hearts keep their normal toggle look (wishlist.css keys off this).
      document.getElementById('wishlist-items-container').setAttribute('data-shared', '');
      ['wishlist-share-trigger', 'wishlist-login-trigger', 'wishlist-login-note'].forEach(function (id) {
        var el = document.getElementById(id);
        if (el) el.hidden = true;
      });
      var title = document.getElementById('wishlist-page-title');
      if (title && title.getAttribute('data-shared-title')) title.textContent = title.getAttribute('data-shared-title');
      return;
    }

    var trigger = document.getElementById('wishlist-share-trigger');
    var dialog = document.getElementById('wishlist-share-dialog');
    if (!trigger || !dialog) return;

    var status = document.getElementById('wishlist-share-status');
    var actions = document.getElementById('wishlist-share-actions');
    var input = document.getElementById('wishlist-share-url');
    var copyBtn = document.getElementById('wishlist-share-copy');
    var nativeBtn = document.getElementById('wishlist-share-native');
    var shareUrl = '';

    function setStatus(text, isError) {
      status.textContent = text;
      status.hidden = !text;
      status.classList.toggle('is-error', !!isError);
    }

    function getPublicId() {
      if (!app || !app.apis || !app.apis.wishlist) return Promise.reject(new Error('Wishlist King SDK not ready'));
      return Promise.resolve(app.apis.wishlist.loadWishlist({ wishlistId: 'mine', cacheStrategy: 'no-store' }))
        .then(function (r) { return r && r.wishlist; });
    }

    trigger.addEventListener('click', function () {
      shareUrl = '';
      actions.hidden = true;
      nativeBtn.hidden = true;
      setStatus('Preparing your link…');
      if (dialog.showModal) dialog.showModal(); else dialog.setAttribute('open', '');

      if (!effectiveCount()) { setStatus('Add items to your wishlist before sharing it.', true); return; }

      getPublicId().then(function (wl) {
        if (!wl || !wl.publicId) { setStatus('Add items to your wishlist before sharing it.', true); return; }
        shareUrl = window.location.origin + window.location.pathname + '?wishlist=' + encodeURIComponent(wl.publicId);
        input.value = shareUrl;
        copyBtn.textContent = 'Copy Link';
        setStatus('');
        actions.hidden = false;
        nativeBtn.hidden = !navigator.share;
      }).catch(function (e) {
        logErr(e);
        setStatus('Sorry, we couldn’t create your share link. Please try again.', true);
      });
    });

    copyBtn.addEventListener('click', function () {
      if (!shareUrl) return;
      function done() { copyBtn.textContent = 'Copied!'; }
      function fallback() { input.select(); try { document.execCommand('copy'); done(); } catch (e) { logErr(e); } }
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(shareUrl).then(done, fallback);
      else fallback();
    });

    nativeBtn.addEventListener('click', function () {
      if (!shareUrl || !navigator.share) return;
      navigator.share({ title: document.title, text: 'Check out my wishlist', url: shareUrl }).catch(function () {});
    });

    function close() { if (dialog.close) dialog.close(); else dialog.removeAttribute('open'); }
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog || (e.target.closest && e.target.closest('[data-wishlist-share-close]'))) close();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initPageHeader);
  else initPageHeader();
})();
